/**
 * Comparison Cell Selector Component
 *
 * A compact altitude × category grid for filtering which items are eligible in
 * a session.  Mirrors the look of ItemTypes.tsx but shows counts instead of
 * item chips.
 *
 * Differences from a plain hardcoded grid:
 *  - Loads workspace custom categories via useItemCategoriesStore so custom
 *    categories appear alongside the six builtins.
 *  - Merges per-category altitude-label overrides (cat.altitudeLabels) so
 *    workspace-customised type names show up correctly.
 *
 * Interaction:
 *  - Single click   → replace selection with this cell
 *  - Ctrl/Cmd+click → toggle cell in/out of multi-select
 *  - Touch screen   → every tap toggles a cell in or out: there is no Ctrl or
 *                     Cmd key to hold (audit 2026-11-02, #42)
 */

import { useMemo, useEffect } from 'react';
import type { AltitudeLevel } from '../../../types/prioritization/itemLifecycle';
import type { GridCellFilter } from '../../../types/prioritization/session';
import { ALTITUDE_LABELS, ALTITUDE_LEVELS_ORDERED } from '../../../lib/altitudeValidation';
import { useItemCategoriesStore } from '../../../stores/prioritization/itemCategoriesStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useKeyboardShortcutHints } from '../../../hooks/useKeyboardShortcutHints';
import { resolveCategoryHexMap, CATEGORY_HEX_FALLBACK } from '../../../lib/itemCategoryColors';

// ============================================================================
// Constants — builtin defaults (same as ItemTypes.tsx)
// ============================================================================

const BUILTIN_CATEGORIES = [
  'problem',
  'opportunity',
  'risk',
  'solution',
  'commitment',
  'investment',
] as const;

const BUILTIN_LABELS: Record<string, string> = {
  problem:    'Problem',
  opportunity:'Opportunity',
  risk:       'Risk',
  solution:   'Solution',
  commitment: 'Commitment',
  investment: 'Investment',
};

const BUILTIN_TYPE_NAMES: Record<string, Record<AltitudeLevel, string>> = {
  problem:    { 5: 'Root Cause', 4: 'Pattern',       3: 'Gap',        2: 'Defect',      1: 'Bug'         },
  opportunity:{ 5: 'Strategy',  4: 'Program',        3: 'Initiative', 2: 'Prospect',    1: 'Quick Win'   },
  risk:       { 5: 'Crisis',    4: 'Threat',         3: 'Exposure',   2: 'Concern',     1: 'Flag'        },
  solution:   { 5: 'Platform',  4: 'Initiative',     3: 'Epic',       2: 'Story',       1: 'Task'        },
  commitment: { 5: 'Mandate',   4: 'Pledge',         3: 'Contract',   2: 'Deliverable', 1: 'Action'      },
  investment: { 5: 'Transformation', 4: 'Portfolio', 3: 'Project',   2: 'Pilot',       1: 'Experiment'  },
};

// ============================================================================
// Types
// ============================================================================

interface GridCellSelectorProps {
  value: GridCellFilter[];
  onChange: (cells: GridCellFilter[]) => void;
  allowMultiple?: boolean;
  disabled?: boolean;
  /** Count of workspace items per cell. Key format: `${altitudeLevel}-${candidateType}` */
  cellCounts?: Record<string, number>;
  /** Override the header label. Defaults to "Candidate Filter". */
  label?: string;
  /** Override the header sub-description (shown when no cells selected). Defaults to "Select cells to restrict which items are eligible". */
  description?: string;
}

// ============================================================================
// Helpers
// ============================================================================

function getCellKey(level: AltitudeLevel, cat: string): string {
  return `${level}-${cat}`;
}

/** Return a CSS color value for a given opacity, given a hex base color. */
function hexWithOpacity(hex: string, opacity: number): string {
  // Expand shorthand hex
  const full = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;
  const r = parseInt(full.slice(1, 3), 16);
  const g = parseInt(full.slice(3, 5), 16);
  const b = parseInt(full.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}

// ============================================================================
// Component
// ============================================================================

export function GridCellSelector({
  value,
  onChange,
  allowMultiple = true,
  disabled = false,
  cellCounts,
  label = 'Candidate Filter',
  description = 'Select cells to restrict which items are eligible',
}: GridCellSelectorProps) {
  // No Ctrl or Cmd key on a phone or a touch-only tablet: there a tap toggles.
  const tapToggles = !useKeyboardShortcutHints();
  // ── Load workspace categories (for custom categories + label overrides) ──
  const { categories, fetchCategories } = useItemCategoriesStore();
  const workspaceId = getCurrentWorkspaceId();

  useEffect(() => {
    if (workspaceId) fetchCategories(workspaceId);
  }, [workspaceId, fetchCategories]);

  // ── Build displayCategories: builtins + active custom (same as ItemTypes) ──
  const displayCategories = useMemo<string[]>(() => {
    const customSlugs = categories
      .filter(c => !c.isBuiltin && c.isActive)
      .map(c => c.slug);
    return [
      ...BUILTIN_CATEGORIES,
      ...customSlugs.filter(s => !(BUILTIN_CATEGORIES as readonly string[]).includes(s)),
    ];
  }, [categories]);

  // ── Build label map (builtin defaults + custom stored labels) ──
  const categoryLabelMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = { ...BUILTIN_LABELS };
    for (const c of categories) {
      if (!map[c.slug]) map[c.slug] = c.label;
    }
    return map;
  }, [categories]);

  // ── Build hex color map (canonical hex + custom stored hex) ──
  const categoryHexMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = resolveCategoryHexMap();
    for (const c of categories) {
      if (!map[c.slug] && c.color) map[c.slug] = c.color;
    }
    return map;
  }, [categories]);

  // ── Merge type names: builtin defaults overridden by workspace labels ──
  const typeNames = useMemo<Record<string, Record<AltitudeLevel, string>>>(() => {
    const merged: Record<string, Record<AltitudeLevel, string>> = {};
    for (const cat of displayCategories) {
      merged[cat] = { ...(BUILTIN_TYPE_NAMES[cat] ?? {}) } as Record<AltitudeLevel, string>;
    }
    for (const c of categories) {
      if (!c.altitudeLabels) continue;
      if (!merged[c.slug]) merged[c.slug] = {} as Record<AltitudeLevel, string>;
      for (const lvl of [5, 4, 3, 2, 1] as const) {
        if (c.altitudeLabels[lvl]) merged[c.slug][lvl] = c.altitudeLabels[lvl]!;
      }
    }
    return merged;
  }, [categories, displayCategories]);

  // ── Selection state ──────────────────────────────────────────────────────
  const selectedKeys = useMemo(
    () => new Set(value.map(c => getCellKey(c.altitudeLevel, c.itemCategory))),
    [value],
  );

  const handleCellClick = (
    level: AltitudeLevel,
    cat: string,
    event: React.MouseEvent,
  ) => {
    if (disabled) return;
    const key = getCellKey(level, cat);
    const isSelected = selectedKeys.has(key);
    const isMulti = allowMultiple && (tapToggles || event.ctrlKey || event.metaKey);

    if (isMulti) {
      onChange(
        isSelected
          ? value.filter(c => getCellKey(c.altitudeLevel, c.itemCategory) !== key)
          : [...value, { altitudeLevel: level, itemCategory: cat as GridCellFilter['itemCategory'] }],
      );
    } else {
      onChange(
        isSelected && value.length === 1
          ? []
          : [{ altitudeLevel: level, itemCategory: cat as GridCellFilter['itemCategory'] }],
      );
    }
  };

  // Total items eligible given current selection
  const totalSelected = useMemo(() => {
    if (!cellCounts || value.length === 0) return null;
    return value.reduce(
      (sum, c) => sum + (cellCounts[getCellKey(c.altitudeLevel, c.itemCategory)] ?? 0),
      0,
    );
  }, [cellCounts, value]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{label}</h4>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
            {value.length === 0
              ? description
              : totalSelected !== null
              ? `${value.length} cell${value.length !== 1 ? 's' : ''} selected — ${totalSelected} item${totalSelected !== 1 ? 's' : ''} eligible`
              : `${value.length} cell${value.length !== 1 ? 's' : ''} selected`}
          </p>
        </div>
        {value.length > 0 && (
          <button
            onClick={() => !disabled && onChange([])}
            disabled={disabled}
            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50 transition-colors"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          {/* Column headers */}
          <thead>
            <tr>
              {/* Corner — "Altitude" label mirroring ItemTypes */}
              <th className="p-2 w-20 text-left border-b border-r border-[var(--rule)] dark:border-[var(--rule)]">
                <span className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wider">
                  Altitude
                </span>
              </th>
              {displayCategories.map(cat => {
                const hex = categoryHexMap[cat] ?? CATEGORY_HEX_FALLBACK;
                const label = categoryLabelMap[cat] ?? cat;
                return (
                  <th
                    key={cat}
                    className="p-2 text-center border-b border-r border-[var(--rule)] dark:border-[var(--rule)] last:border-r-0"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: hex }}
                      />
                      <span
                        className="text-xs font-semibold"
                        style={{ color: hex }}
                      >
                        {label}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {ALTITUDE_LEVELS_ORDERED.map(level => (
              <tr key={level}>
                {/* Row header — "Altitude N" and the level's generic name as its tag */}
                <td className="p-1.5 border-r border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-b-0">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider leading-tight">
                      Altitude {level}
                    </span>
                    <span className="text-[9px] text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-tight mt-0.5">
                      {ALTITUDE_LABELS[level]}
                    </span>
                  </div>
                </td>

                {/* Data cells */}
                {displayCategories.map(cat => {
                  const key = getCellKey(level, cat);
                  const isSelected = selectedKeys.has(key);  // selectedKeys built from itemCategory
                  const count = cellCounts?.[key] ?? 0;
                  const cellName = typeNames[cat]?.[level] ?? '';
                  const hex = categoryHexMap[cat] ?? CATEGORY_HEX_FALLBACK;
                  const isEmpty = count === 0;

                  const baseBg = isEmpty
                    ? 'var(--surface-raised)' // empty muted — flips with the real light/dark theme
                    : hexWithOpacity(hex, isSelected ? 0.22 : 0.10);
                  const borderColor = isEmpty
                    ? 'var(--rule)'
                    : hexWithOpacity(hex, isSelected ? 0.8 : 0.25);

                  return (
                    <td key={key} className="p-0.5 border-b border-[var(--rule)] dark:border-[var(--rule)] last-of-type:border-b-0">
                      <button
                        onClick={e => handleCellClick(level, cat, e)}
                        disabled={disabled}
                        title={`Altitude ${level} · ${categoryLabelMap[cat]} — ${cellName}${count > 0 ? ` (${count} item${count !== 1 ? 's' : ''})` : ' (empty)'}${allowMultiple && !tapToggles ? '\nCtrl/Cmd+click for multi-select' : ''}`}
                        style={{
                          backgroundColor: baseBg,
                          borderColor,
                          outline: isSelected ? `2px solid ${hexWithOpacity(hex, 0.7)}` : 'none',
                          outlineOffset: '1px',
                        }}
                        className={`
                          w-full h-14  border transition-all text-left px-2 py-1.5 flex flex-col justify-between
                          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {/* Cell type name */}
                        <span
                          style={{ color: isEmpty ? 'var(--text-ter)' : hexWithOpacity(hex, isSelected ? 0.9 : 0.65) }}
                          className="text-[9px] font-medium leading-tight truncate block"
                        >
                          {cellName}
                        </span>

                        {/* Item count */}
                        <span
                          style={{ color: isEmpty ? 'var(--text-dim)' : hexWithOpacity(hex, isSelected ? 1 : 0.8) }}
                          className="text-sm font-bold leading-none"
                        >
                          {cellCounts !== undefined ? count : ''}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Hint */}
      {allowMultiple && !disabled && (
        <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          {tapToggles
            ? 'Tap a cell to add it or take it out'
            : 'Hold Ctrl/Cmd and click to select multiple cells'}
        </p>
      )}
    </div>
  );
}

export default GridCellSelector;
